import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Collapse,
  Box,
  CircularProgress,
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Badge from '@/components/Badge';
import { ConsolidatedSkill } from '@/services/workersV2API';
import { axiosRubyClient } from '@/util/axios/axiosClient';

type Props = {
  skill: ConsolidatedSkill;
  workerId: number;
  onUpdate: () => void;
};

const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const getStatusBadge = (
  status: string | null | undefined,
): { label: string; status: 'theme' | 'danger' | 'neutral' | 'warning' } => {
  if (!status) return { label: '-', status: 'neutral' };
  switch (status) {
    case 'completed':
    case 'passed':
      return { label: status, status: 'theme' };
    case 'failed':
      return { label: status, status: 'danger' };
    case 'pending':
      return { label: status, status: 'neutral' };
    default:
      return { label: status, status: 'warning' };
  }
};

const AIAssessmentTab = ({ skill, workerId, onUpdate }: Props) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [loadingAction, setLoadingAction] = useState<
    'passed' | 'failed' | null
  >(null);

  const ai = skill.ai_assessment;

  const handleMarkAssessment = async (passed: boolean) => {
    if (!skill.skill_request) return;
    const action = passed ? 'passed' : 'failed';
    setLoadingAction(action);

    try {
      await axiosRubyClient.patch(
        `/api/admin/skill_requests/${skill.skill_request_id}/assessment`,
        { passed },
      );
      onUpdate();
    } catch (error) {
      console.error('Error marking assessment:', error);
    } finally {
      setLoadingAction(null);
    }
  };

  return (
    <Card>
      <Card.Section>
        <Body weight="semibold" style={{ marginBottom: '16px' }}>
          AI Assessment
        </Body>

        {!ai ? (
          <Small color="lighter">No AI assessment found</Small>
        ) : (
          <>
            {/* Summary Table */}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Small weight="semibold">ID</Small>
                    </TableCell>
                    <TableCell>
                      <Small>{ai.id}</Small>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Small weight="semibold">External ID</Small>
                    </TableCell>
                    <TableCell>
                      <Small>{ai.external_id}</Small>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Small weight="semibold">Status</Small>
                    </TableCell>
                    <TableCell>
                      <Badge
                        label={getStatusBadge(ai.status).label}
                        status={getStatusBadge(ai.status).status}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Small weight="semibold">Total Score</Small>
                    </TableCell>
                    <TableCell>
                      <Small>
                        {ai.total_score !== null ? ai.total_score : '-'}
                      </Small>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Small weight="semibold">Result Status</Small>
                    </TableCell>
                    <TableCell>
                      <Badge
                        label={getStatusBadge(ai.result_status).label}
                        status={getStatusBadge(ai.result_status).status}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Small weight="semibold">Final Status</Small>
                    </TableCell>
                    <TableCell>
                      <Badge
                        label={getStatusBadge(ai.final_status).label}
                        status={getStatusBadge(ai.final_status).status}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Small weight="semibold">Language</Small>
                    </TableCell>
                    <TableCell>
                      <Small>{ai.language}</Small>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Small weight="semibold">Started At</Small>
                    </TableCell>
                    <TableCell>
                      <Small>{formatDateTime(ai.started_at)}</Small>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Small weight="semibold">Completed At</Small>
                    </TableCell>
                    <TableCell>
                      <Small>{formatDateTime(ai.completed_at)}</Small>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* Expandable Details */}
            <Box sx={{ mt: 2 }}>
              <Button
                variant="text"
                size="small"
                onClick={() => setDetailsOpen(!detailsOpen)}
                endIcon={detailsOpen ? <ExpandLess /> : <ExpandMore />}
              >
                {detailsOpen ? 'Hide Details' : 'Show Details'}
              </Button>
              <Collapse in={detailsOpen}>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ mt: 1 }}
                >
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <Small weight="semibold">Reviewed At</Small>
                        </TableCell>
                        <TableCell>
                          <Small>{formatDateTime(ai.reviewed_at)}</Small>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Small weight="semibold">Reviewed By</Small>
                        </TableCell>
                        <TableCell>
                          <Small>{ai.reviewed_by_name || '-'}</Small>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Small weight="semibold">Dial Count</Small>
                        </TableCell>
                        <TableCell>
                          <Small>{ai.dial_count}</Small>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Small weight="semibold">Worker Locked</Small>
                        </TableCell>
                        <TableCell>
                          <Badge
                            label={ai.is_worker_locked ? 'Yes' : 'No'}
                            status={ai.is_worker_locked ? 'danger' : 'theme'}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Small weight="semibold">Lock Reason</Small>
                        </TableCell>
                        <TableCell>
                          <Small>{ai.lock_reason || '-'}</Small>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Small weight="semibold">Created At</Small>
                        </TableCell>
                        <TableCell>
                          <Small>{formatDateTime(ai.created_at)}</Small>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapse>
            </Box>

            {/* Admin Actions */}
            {skill.skill_request && (
              <Stack gap={12} style={{ marginTop: '16px' }}>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  disabled={loadingAction !== null}
                  onClick={() => handleMarkAssessment(true)}
                  startIcon={
                    loadingAction === 'passed' ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  Mark Passed
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  disabled={loadingAction !== null}
                  onClick={() => handleMarkAssessment(false)}
                  startIcon={
                    loadingAction === 'failed' ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  Mark Failed
                </Button>
              </Stack>
            )}
          </>
        )}
      </Card.Section>
    </Card>
  );
};

export default AIAssessmentTab;
